const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

export const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toList = (data) => {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (Array.isArray(data?.result)) return data.result.filter(Boolean);
  if (Array.isArray(data?.result?.data)) return data.result.data.filter(Boolean);
  if (Array.isArray(data?.data)) return data.data.filter(Boolean);
  if (Array.isArray(data?.data?.result)) return data.data.result.filter(Boolean);
  if (Array.isArray(data?.items)) return data.items.filter(Boolean);
  return [];
};

export const parseDashboardWidgets = (data) => {
  const source = data?.result ?? data ?? {};

  return {
    totalReferrals: toAmount(pick(source, 'totalReferrals', 'TotalReferrals', 'totalClients', 'TotalClients')),
    totalClients: toAmount(pick(source, 'totalClients', 'TotalClients', 'totalReferrals', 'TotalReferrals')),
    activeClients: toAmount(pick(source, 'activeClients', 'ActiveClients')),
    newRegistrationsToday: toAmount(
      pick(source, 'newRegistrationsToday', 'NewRegistrationsToday', 'newRegistrationToday', 'NewRegistrationToday')
    ),
    totalDeposits: toAmount(pick(source, 'totalDeposits', 'TotalDeposits', 'totalClientDeposit', 'TotalClientDeposit')),
    totalClientDeposit: toAmount(
      pick(source, 'totalClientDeposit', 'TotalClientDeposit', 'totalRevenue', 'TotalRevenue', 'totalDeposits', 'TotalDeposits')
    ),
    totalTradingVolume: toAmount(
      pick(source, 'totalTradingVolume', 'TotalTradingVolume', 'totalLots', 'TotalLots', 'tradingVolume', 'TradingVolume')
    ),
    totalCommissionEarned: toAmount(
      pick(
        source,
        'totalEarning',
        'TotalEarning',
        'paidEarnings',
        'PaidEarnings',
        'totalCommissionEarned',
        'TotalCommissionEarned',
        'totalCommission',
        'TotalCommission'
      )
    ),
    totalRevenue: toAmount(
      pick(source, 'totalClientDeposit', 'TotalClientDeposit', 'totalRevenue', 'TotalRevenue')
    ),
    pendingCommission: toAmount(
      pick(
        source,
        'pendingEarnings',
        'PendingEarnings',
        'pendingCommission',
        'PendingCommission',
        'holdEarning',
        'HoldEarning'
      )
    ),
    paidCommission: toAmount(
      pick(source, 'totalEarning', 'TotalEarning', 'paidEarnings', 'PaidEarnings', 'paidCommission', 'PaidCommission')
    ),
    totalEarning: toAmount(
      pick(source, 'totalEarning', 'TotalEarning', 'paidEarnings', 'PaidEarnings', 'paidCommission', 'PaidCommission')
    ),
    availableBalance: toAmount(pick(source, 'availableBalance', 'AvailableBalance', 'balance', 'Balance')),
    totalEarned: toAmount(
      pick(source, 'totalEarned', 'TotalEarned', 'totalEarning', 'TotalEarning', 'profit', 'Profit')
    ),
    withdrawn: toAmount(
      pick(source, 'withdrawn', 'Withdrawn', 'withdrawal', 'Withdrawal', 'totalWithdrawal', 'TotalWithdrawal')
    ),
    holdEarning: toAmount(pick(source, 'holdEarning', 'HoldEarning')),
    nextRebatePayment: toAmount(pick(source, 'nextRebatePayment', 'NextRebatePayment')),
    commissionGenerated: toAmount(
      pick(source, 'commissionGenerated', 'CommissionGenerated', 'generated', 'Generated')
    ),
    pendingValidation: toAmount(
      pick(source, 'pendingValidation', 'PendingValidation', 'pendingValidationCommission', 'PendingValidationCommission')
    ),
    unreleased: toAmount(pick(source, 'unreleased', 'Unreleased')),
    released: toAmount(pick(source, 'released', 'Released')),
    partnerUrl: pick(source, 'partnerUrl', 'PartnerUrl', 'referralLink', 'ReferralLink'),
    partnerCode: pick(source, 'partnerCode', 'PartnerCode', 'affiliateCode', 'AffiliateCode'),
    totalClicks: toAmount(pick(source, 'totalClicks', 'TotalClicks')),
    totalConversions: toAmount(pick(source, 'totalConversions', 'TotalConversions')),
    topClients: toList(pick(source, 'topClients', 'TopClients', 'topPerformingClients', 'TopPerformingClients')),
    referralsGrowth: pick(source, 'referralsGrowth', 'ReferralsGrowth', 'totalReferralsGrowth'),
    revenueGrowth: pick(source, 'revenueGrowth', 'RevenueGrowth', 'totalRevenueGrowth'),
    paidGrowth: pick(source, 'paidGrowth', 'PaidGrowth', 'paidCommissionGrowth'),
    pendingGrowth: pick(source, 'pendingGrowth', 'PendingGrowth', 'pendingCommissionGrowth'),
    clicksGrowth: pick(source, 'clicksGrowth', 'ClicksGrowth'),
    conversionsGrowth: pick(source, 'conversionsGrowth', 'ConversionsGrowth', 'registrationsGrowth'),
    conversionRateGrowth: pick(source, 'conversionRateGrowth', 'ConversionRateGrowth'),
  };
};

export const buildEarningsSegmentsFromWidgets = (widgets) => {
  if (!widgets) return [];

  const paid = toAmount(widgets.paidCommission);
  const pending = toAmount(widgets.pendingCommission);
  const total = paid + pending;
  if (total <= 0) return [];

  return [
    { label: 'Paid Earnings', amount: paid, percentage: (paid / total) * 100 },
    { label: 'Pending Earnings', amount: pending, percentage: (pending / total) * 100 },
  ].filter((seg) => seg.amount > 0);
};

export const formatGrowth = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const sign = num >= 0 ? '↑' : '↓';
  return `${sign} ${Math.abs(num).toFixed(1)}%`;
};

export const parseMonthwiseChart = (data) => {
  const list = toList(data);
  if (!list.length) return null;

  const labels = [];
  const earned = [];
  const pending = [];
  const paid = [];

  list.forEach((item) => {
    labels.push(item.month || item.label || item.name || item.monthName || '');
    earned.push(toAmount(item.total || item.totalCommission || item.earned || item.amount || item.commissionEarned));
    pending.push(toAmount(item.pending || item.pendingCommission));
    paid.push(toAmount(item.paid || item.paidCommission || item.withdrawn));
  });

  return labels.some(Boolean) ? { labels, earned, pending, paid } : null;
};

export const parseClientsList = (data) =>
  toList(data).map((client, index) => {
    const kycRaw = (client.docsStatus || client.kycStatus || '').toString().toLowerCase();
    const statusRaw = (client.status || client.accountStatus || '').toString().toLowerCase();

    return {
      id: client.clientUserId || client.id || index,
      name: client.fullName || client.clientName || '—',
      date: client.createdDateTime || client.registrationDate || client.regDate || '',
      kyc: kycRaw.includes('verif') ? 'verified' : 'pending',
      status: statusRaw.includes('inactive') || statusRaw.includes('disabled') ? 'inactive' : 'active',
      account: client.accountNo || client.accountNumber || client.loginId || '—',
      dep: toAmount(client.totalDeposit || client.deposits || client.deposit || client.totalDeposits),
      wd: toAmount(client.totalWithdrawal || client.withdrawal || client.totalWithdrawals),
      lots: toAmount(client.totalLots || client.lots || client.volume || client.tradingVolume),
      commission: toAmount(client.totalCommission || client.commission),
    };
  });

export const parsePartnerPackages = (data) => {
  const list = toList(data);
  return list.map((pkg) => ({
    name: pkg.levelName || pkg.typeName || pkg.name || '—',
    desc:
      pkg.description ||
      (pkg.activeClients != null ? `Up to ${pkg.activeClients} Clients` : pkg.maximumLots != null ? `${pkg.maximumLots} Lots` : ''),
    pct:
      pkg.commissionPercentage != null
        ? `${pkg.commissionPercentage}%`
        : pkg.maximumCommission != null
          ? `$${pkg.maximumCommission}/LOT`
          : '—',
    commission:
      pkg.commissionPercentage != null
        ? `${pkg.commissionPercentage}%`
        : pkg.maximumCommission != null
          ? `$${pkg.maximumCommission}`
          : '—',
    minDeposit:
      pkg.minimumDeposit != null
        ? formatCurrencyValue(pkg.minimumDeposit)
        : pkg.minDeposit != null
          ? formatCurrencyValue(pkg.minDeposit)
          : pkg.minimumLots != null
            ? `${pkg.minimumLots} Lots`
            : '—',
    description: pkg.description || (pkg.activeClients != null ? `Up to ${pkg.activeClients} active clients` : '—'),
  }));
};

const formatCurrencyValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

export const parseSymbolBreakdown = (data) => {
  const list = toList(data);
  if (!list.length) return [];
  return list.map((item) => ({
    label: item.symbol || item.category || item.name || item.label || '—',
    percentage: toAmount(item.percentage || item.percent || item.share),
    amount: toAmount(item.amount || item.value || item.total),
  }));
};

export const parsePerformanceTrend = (data) => {
  const list = toList(data);
  if (!list.length) return null;

  const labels = [];
  const clicks = [];
  const registrations = [];

  list.forEach((item) => {
    labels.push(item.month || item.label || item.day || item.date || '');
    clicks.push(
      toAmount(item.clicks || item.totalClicks || item.commission || item.commissionAmount || item.amount)
    );
    registrations.push(
      toAmount(item.registrations || item.totalRegistrations || item.conversions || item.clients || item.partners)
    );
  });

  if (!labels.some(Boolean)) return null;
  return { labels, clicks, registrations };
};

export const calcConversionRate = (clicks, conversions) => {
  if (!clicks || clicks <= 0) return 0;
  return (conversions / clicks) * 100;
};

export const buildTopClients = (clients, widgetTopClients = []) => {
  if (widgetTopClients.length > 0) {
    return widgetTopClients.slice(0, 5).map((client, index) => ({
      rank: index + 1,
      name: client.fullName || client.clientName || client.name || '—',
      deposits: toAmount(client.totalDeposit || client.deposits || client.deposit),
      lots: toAmount(client.totalLots || client.lots || client.volume),
      commission: toAmount(client.totalCommission || client.commission),
    }));
  }

  return [...clients]
    .filter((client) => client.commission > 0 || client.dep > 0 || client.lots > 0)
    .sort((a, b) => (b.commission || b.dep || b.lots) - (a.commission || a.dep || a.lots))
    .slice(0, 5)
    .map((client, index) => ({
      rank: index + 1,
      name: client.name,
      deposits: client.dep,
      lots: client.lots,
      commission: client.commission,
    }));
};

export const formatDisplayDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const parseAffiliateList = (data) =>
  toList(data).map((item) => ({
    id: item.affiliateCode || item.partnerCode || item.userId || '',
    clients: toAmount(item.totalClients || item.clientCount || item.clients || item.activeClients),
    name: item.fullName || item.name || item.affiliateName || '',
  }));

export const parsePerformanceWidgets = (data) => {
  const source = data?.result ?? data ?? {};
  return {
    commissionGrowth: pick(source, 'commissionGrowth', 'CommissionGrowth'),
    affiliateGrowth: pick(source, 'affiliateGrowth', 'AffiliateGrowth'),
    clientGrowth: pick(source, 'clientGrowth', 'ClientGrowth'),
  };
};
